import {MenuPanels} from '../../models/UIModel.js';
import {ShoppeBase} from './shoppe-base.js';
import {
    Column,
    Container,
    Image,
    Panel,
    Panel9Slice,
    Row,
    Text,
} from '@wonderlandengine/react-ui/components';
import {goToPreviousMenu} from '../utils/goToPreviousMenu.js';
import {Carousel, CarouselItem} from '../components/carousel.js';
import React, {useEffect, useRef, useState} from 'react';
import {StoreInventoryItems} from '../../enums/StoreInventoryItems.js';
import {SliderItem} from '../components/SliderComponent.js';

import {StorePowerupsData} from '../../components/store-data/powerups/StorePowerupsData.js';
import {TransactionalNames} from '../../enums/TransactionalNames.js';
import {Statistics} from '../../components/statistics.js';
import {Localization} from '../../utils/Localization.js';
import {formatString, insertNewlines, setAlpha} from '../utils/utils.js';
import {Align, FlexDirection, Justify} from '@wonderlandengine/react-ui';
import {colorSwatch} from '../utils/colorSwatch.js';
import {useRotBMaterialContext} from '../utils/rotb-material-context.js';
import {StoreManager} from '../../components/store-manager.js';
import {formatNumber} from '../../utils/Utils.js';
import {Header} from '../components/header.js';
import {BuyButton} from '../components/buy-button.js';
import {CoinButton} from '../components/coin-button.js';
import {buyItem} from '../utils/buyItem.js';
import {UnlockManager} from '../../components/unlock-manager.js';
import {Texture} from '@wonderlandengine/api';
import {UiTexturesManager} from '../components/ui-textures-manager.js';
import {PowerupSelectItem} from '../../components/store-data/powerups/PowerupSelectItem.js';
import {FlatOpaqueTextured} from '../../types/materials.js';
import {PowerupMaterialType, usePowerupMaterial} from '../hooks/usePowerupMaterial.js';

function isStoreItemLocked(storeItem: StoreInventoryItems) {
    const data = StorePowerupsData.instance.data.get(storeItem);
    return Statistics.instance.getTransactional(data.stat) == 0;
}

function lockImage(storeItem: StoreInventoryItems, image: Texture): Texture {
    if (!isStoreItemLocked(storeItem)) {
        return image;
    } else return UiTexturesManager.instance.buttonLock;
}

export class PowerupStorePopUpParams {
    static selectedItem: StoreInventoryItems = null;
    static isPerk: boolean;
    static icon: Texture;
}

export const ShoppePowerupsPopup = () => {
    const mats = useRotBMaterialContext();

    const [refreshKey, setRefreshKey] = useState(0);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [levelText, setLevelText] = useState('');
    const [inventoryAmount, setInventoryAmount] = useState('');
    const [upgradeDescription, setUpgradeDescription] = useState('');
    const [unlockDescription, setUnlockDescription] = useState('');
    const [inventoryStat, setInventoryStat] = useState<TransactionalNames>(null);
    const [upgradeStat, setUpgradeStat] = useState(null);
    const [upgradeCost, setUpgradeCost] = useState(0);
    const [isLocked, setIsLocked] = useState(true);
    const [upgradeMaxed, setUpgradeMaxed] = useState(false);
    const [isPerk, setIsPerk] = useState(false);
    const [icon, setIcon] = useState<Texture>(null);
    const [divots, setDivots] = useState<boolean[]>([false, false, false, false, false]);
    const divotEmpty = usePowerupMaterial(
        PowerupMaterialType.DivotEmpty,
        colorSwatch.UpgradeDivotEmpty
    );

    const divotFull = usePowerupMaterial(
        PowerupMaterialType.DivotFull,
        colorSwatch.UpgradeDivotFull
    );
    useEffect(() => {
        setIsPerk(PowerupStorePopUpParams.isPerk);
        setIcon(PowerupStorePopUpParams.icon);
        const data = StorePowerupsData.instance.data.get(
            PowerupStorePopUpParams.selectedItem
        ) as PowerupSelectItem;

        const descriptions = StorePowerupsData.instance.descriptions.get(
            PowerupStorePopUpParams.selectedItem
        );

        setTitle(Localization.instance.get(data.title));
        setDescription(Localization.instance.get(descriptions.levelUpDescriptions[0]));
        const upgradeLevel = StoreManager.instance.getUpgradeLevel(data.stat);
        let currentDescriptionText = ' ';
        const d = [];
        for (let i = 0; i < 5; i++) {
            if (i < upgradeLevel) {
                d.push(true);
            } else {
                d.push(false);
            }
        }
        setDivots(d);

        const locked = isStoreItemLocked(PowerupStorePopUpParams.selectedItem);
        if (locked) {
            setInventoryAmount(Localization.instance.get('Locked'));
            setLevelText('');
            setUpgradeMaxed(false);
            const upgradeCoinCost = StoreManager.instance.getCoinCost(data.stat);
            setUpgradeCost(upgradeCoinCost);
            setUpgradeDescription('');

            const unlockInstruct = UnlockManager.instance.getInstructionsForUnlock(
                data.lockFlag,
                0
            );
            setUnlockDescription(unlockInstruct.instructions);
        } else {
            if (upgradeLevel > 0) {
                const currentInventory = Statistics.instance.getTransactional(
                    data.inventoryStat
                );
                if (currentInventory == 0) {
                    // do some bouncy thing
                } else {
                    // reset bouncy thing
                }
                setInventoryAmount(
                    formatString(
                        Localization.instance.get('YouGot'),
                        formatNumber(currentInventory)
                    )
                );
                setLevelText(
                    formatString(Localization.instance.get('UpgradeLevel'), upgradeLevel)
                );

                const upgradeCoinCost = StoreManager.instance.getCoinCost(data.stat);
                setUpgradeCost(upgradeCoinCost);
                if (upgradeLevel < descriptions.levelUpDescriptions.length) {
                    setUpgradeMaxed(false);
                    const description = Localization.instance.get(
                        descriptions.levelUpDescriptions[upgradeLevel]
                    );
                    if (upgradeLevel > 0) {
                        const nextUpgrade = Localization.instance.get('NextUpgradeFormat');
                        currentDescriptionText = formatString(nextUpgrade, description);
                    }
                }
                // maxed out
                else {
                    currentDescriptionText = Localization.instance.get(
                        descriptions.levelUpDescriptions[
                            descriptions.levelUpDescriptions.length - 1
                        ]
                    );
                    setUpgradeMaxed(true);
                }
            } else {
                setLevelText('');
            }
        }
        setUpgradeDescription(currentDescriptionText);
        setInventoryStat(data.inventoryStat);
        setUpgradeStat(data.stat);
        setIsLocked(locked);
    }, [refreshKey]);

    useEffect(() => {
        const changeCallback = () => {
            setRefreshKey((prev) => prev + 1);
        };
        Statistics.instance.registerChangedCallback(changeCallback);
        return () => {
            Statistics.instance.unRegisterChangedCallback(changeCallback);
        };
    }, []);

    return (
        <ShoppeBase
            title={title}
            onOkay={() => goToPreviousMenu(MenuPanels.StorePowerupsPopup)}
        >
            <Column gap={20}>
                <Row padding={10} gap={20}>
                    <Column gap={6}>
                        <Image width={120} height={120} rounding={0} src={icon}></Image>
                        <Row gap={6} marginLeft={6}>
                            {!isLocked &&
                                divots.map((divot, index) => {
                                    return (
                                        <Panel9Slice
                                            key={index}
                                            height={18}
                                            width={18}
                                            rounding={0}
                                            border={8}
                                            borderTextureSize={0.0625}
                                            material={divot ? divotFull : divotEmpty}
                                        ></Panel9Slice>
                                    );
                                })}
                        </Row>
                    </Column>
                    <Column gap={20} justifyContent={Justify.FlexStart}>
                        <Text
                            width={350}
                            textAlign="right"
                            fontSize={24}
                            text={levelText}
                        ></Text>
                        <Text
                            width={350}
                            color={setAlpha(colorSwatch.DescriptionText, '90%')}
                            material={mats.regularTextMaterial}
                            fontSize={20}
                            textAlign="right"
                            textWrap="soft"
                            text={description}
                        ></Text>
                    </Column>
                </Row>
                {!isPerk && (
                    <Container>
                        {!isLocked ? (
                            <Header
                                title={Localization.instance.get('InventoryTitle')}
                                subtitle={inventoryAmount}
                            />
                        ) : (
                            <Header
                                title={Localization.instance.get('UnlockRequirements')}
                            />
                            //TK: Should be centered, but that doesn't work at the moment
                            // https://github.com/WonderlandEngine/wonderland-react-ui/issues/20
                        )}
                        {!isLocked && inventoryStat && (
                            <Row
                                flexGrow={1}
                                padding={10}
                                justifyContent={Justify.SpaceBetween}
                                height={120}
                            >
                                <BuyButton stat={inventoryStat} costIndex={0} />
                                <BuyButton stat={inventoryStat} costIndex={1} />
                                <BuyButton stat={inventoryStat} costIndex={2} />
                            </Row>
                        )}

                        {isLocked && (
                            <Row
                                flexGrow={1}
                                padding={10}
                                justifyContent={Justify.SpaceBetween}
                                height={120}
                            >
                                <Text
                                    color={setAlpha(colorSwatch.DescriptionText, '90%')}
                                    material={mats.regularTextMaterial}
                                    fontSize={18}
                                >
                                    {insertNewlines(unlockDescription, 36)}
                                </Text>
                            </Row>
                        )}
                    </Container>
                )}
                <Container>
                    {isLocked ? (
                        <Header title={Localization.instance.get('UnlockNow')} />
                    ) : (
                        <Header
                            title={upgradeMaxed ? 'UPGRADE MAX' : 'UPGRADE'}
                            subtitle={levelText}
                        />
                    )}
                    <Column
                        height={120}
                        justifyContent={Justify.SpaceEvenly}
                        alignItems={Align.Center}
                    >
                        {!upgradeMaxed && (
                            <CoinButton
                                price={upgradeCost}
                                onClick={() => {
                                    buyItem(upgradeStat, upgradeCost, 1);
                                }}
                            />
                        )}
                        <Text
                            marginTop={20}
                            width={320}
                            flexGrow={1}
                            textAlign="center"
                            color={setAlpha(colorSwatch.DescriptionText, '90%')}
                            material={mats.regularTextMaterial}
                            fontSize={18}
                            text={upgradeDescription}
                            textWrap="soft"
                        ></Text>
                    </Column>
                </Container>
            </Column>
        </ShoppeBase>
    );
};
